import { datadogRum } from '@datadog/browser-rum'
import { CART_MUTATION_TIMEOUT_MS } from './cartMutationTimeout'
import { flushDebouncedCalls, stopWaitingForInFlightCalls } from './debounce'
import { generateDataDogContext } from './dataDog'

/**
 * How long `settlePendingCartWrites` waits for cart writes before giving up.
 * Same bound as `AFTER_ORDER_PUSH_WAIT_MS`: long enough for a normal cart
 * mutation, short enough that a hung one does not keep the shopper waiting.
 */
export const PENDING_CART_WRITES_WAIT_MS = 3000

/**
 * How long a cart write has been in flight before any new wait stops waiting
 * for it: `CART_MUTATION_TIMEOUT_MS`, past which a cart mutation is treated as
 * failed where it is aborted at all (COM-1521). Well above
 * `PENDING_CART_WRITES_WAIT_MS`, so a slow but healthy write, 3 or 10 s out,
 * is still waited for by a new reload rather than cut off by it.
 */
export const PRESUMED_HUNG_CART_WRITE_MS = CART_MUTATION_TIMEOUT_MS

/**
 * Stops waiting for cart writes in flight for `PRESUMED_HUNG_CART_WRITE_MS`
 * or longer, and reports to Datadog once if it gave up on any, with how many
 * and the oldest one's age (COM-1521). Such a write may have hung while no
 * wait was watching it, so no "still pending" report ever named it; this is
 * where it is seen. Run before every drain round (`settlePendingCartWrites`)
 * and before `reloadPage` decides whether to wait. Silent when it gives up on
 * nothing. The writes are not aborted.
 */
export const giveUpOnPresumedHungWrites = (caller: string) => {
  const { count, oldestAgeMs } = stopWaitingForInFlightCalls(
    PRESUMED_HUNG_CART_WRITE_MS,
  )
  if (count === 0) {
    return
  }
  const context = generateDataDogContext({
    title: 'cart writes presumed hung, no longer waited for',
    extraInfo: {
      function: caller,
      count,
      oldestAgeMs: Math.round(oldestAgeMs),
      thresholdMs: PRESUMED_HUNG_CART_WRITE_MS,
    },
  })
  datadogRum.startView(context.viewName)
  datadogRum.addError(
    new Error(`${caller} stopped waiting for presumed hung cart writes`),
    context,
  )
}

/**
 * Drains the add and remove debounce (`flushDebouncedCalls`): sends every
 * write still waiting, waits for every one sent to answer, and repeats while
 * the shopper queues more, until none is waiting or in flight (COM-1500).
 * One bound, `PENDING_CART_WRITES_WAIT_MS`, covers the whole drain, not each
 * round, so writes that keep arriving cannot hold the caller past it. Never
 * rejects, even if the drain does: it reports that and resolves. Past the
 * bound it stops the drain, reports to Datadog and resolves anyway, so a
 * request that never answers cannot block the caller: each caller waits at
 * most `PENDING_CART_WRITES_WAIT_MS`. Writes are given up on at two ages
 * (COM-1521):
 * - when the bound passes, a write in flight for the whole bound,
 *   `PENDING_CART_WRITES_WAIT_MS`, which this wait has just held for that
 *   long;
 * - at the start of every round, a write in flight for
 *   `PRESUMED_HUNG_CART_WRITE_MS` (15 s), reported as presumed hung
 *   (`giveUpOnPresumedHungWrites`). A drain that starts, or begins a round,
 *   once a write is that old does not wait for it. A slow, healthy write 3 to
 *   15 s old is still waited for by a new drain: a reload must not abort it.
 * A drain already waiting on a write when it is given up on is not released:
 * it finishes its own bound. Writes given up on are not aborted, and their
 * answers, if any, still land and still count in `settledDebouncedCalls`.
 * Writes still waiting for their timer are never given up on: a later drain
 * sends them. A write that throws before it is sent is reported and dropped
 * by the drain.
 * Delete-product, clear-cart, prefill and chosen-free-goods writes do not go
 * through the debounce and are not waited for; they are only counted, in
 * `settledCartWrites`, so `refreshCatalog` can read the cart again when one
 * answers under its read (COM-1512).
 */
export const settlePendingCartWrites = async (caller: string) => {
  let timer: ReturnType<typeof setTimeout> | undefined
  const bound = new Promise<'timeout'>((resolve) => {
    timer = setTimeout(() => resolve('timeout'), PENDING_CART_WRITES_WAIT_MS)
  })
  let timedOut = false
  let outcome: 'settled' | 'timeout' | 'failed'
  try {
    outcome = await Promise.race([
      flushDebouncedCalls(
        () => timedOut,
        () => giveUpOnPresumedHungWrites(caller),
      ).then(() => 'settled' as const),
      bound,
    ])
  } catch (error) {
    // Not expected, since the drain reports each throw itself, but the
    // caller must always go on: `reloadPage` reloads after this
    outcome = 'failed'
    const context = generateDataDogContext({
      title: 'draining cart writes failed',
      extraInfo: { function: caller },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(error, context)
  }
  timedOut = outcome !== 'settled'
  clearTimeout(timer)
  if (outcome === 'timeout') {
    stopWaitingForInFlightCalls(PENDING_CART_WRITES_WAIT_MS)
    const context = generateDataDogContext({
      title: 'cart writes still pending after the wait',
      extraInfo: { function: caller, waitMs: PENDING_CART_WRITES_WAIT_MS },
    })
    datadogRum.startView(context.viewName)
    datadogRum.addError(
      new Error(`${caller} went on with cart writes still pending`),
      context,
    )
  }
}

export default settlePendingCartWrites
